'use client'

import DashboardContentWrapper from '@/shared/components/dashboard-content-wrapper'
import Heading from '@/shared/components/ui/typography/heading'
import LocationSelector from '@/shared/components/location-selector'
import { useParams } from 'next/navigation'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useRegion } from '@/shared/hooks/use-region'
import { useStmGroup } from '@/features/stm/hooks/use-stm-weeks'
import { useStmParticipants } from '@/features/stm/hooks/use-stm-participants'
import { GroupDetailsTabs } from '@/features/stm/components/group-detail/GroupDetailsTabs'
import { GroupBackButton } from '@/features/stm/components/group-detail/GroupBackButton'
import { useSession } from '@/features/auth/context/session.context'
import { useRouter } from 'next/navigation'
import { buildRegionUrl } from '@/shared/lib/utils'
import { SEARCH_PARAMS_TAB_KEY } from '@/features/volunteers/constants/volunteer-tab-links'
import { UserRole } from '@/shared/constants/user-roles'
import type { Participant } from '@/features/stm/lib/types'
import { useEffect } from 'react'
import { toast } from 'sonner'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'

const GROUP_CONTACT_ROLES = [UserRole.VOLUNTEER, UserRole.TEAM_LEADER, UserRole.PARTNER]

export default function GroupDetailPage() {
  const params = useParams()
  const groupId = typeof params?.groupId === 'string' ? params.groupId : null
  const { regionId } = useRegionId()
  const { regionSlug } = useRegion()
  const { data: group, isLoading, error } = useStmGroup(groupId, regionId ?? null)
  const { data: participants = [], isLoading: participantsLoading } = useStmParticipants(groupId)
  const { session, isLoading: sessionLoading } = useSession()
  const router = useRouter()
  const groupsHref = regionSlug
    ? buildRegionUrl(regionSlug, '/volunteers', { [SEARCH_PARAMS_TAB_KEY]: 'groups' })
    : `/volunteers?${SEARCH_PARAMS_TAB_KEY}=groups`
  const groupRegionId = group ? (group.regionId ?? group.region ?? null) : null
  const isRegionMismatch =
    regionId != null && groupRegionId != null && Number(groupRegionId) !== Number(regionId)
  const isWrongRegionError =
    !!error &&
    error instanceof Error &&
    error.message.toLowerCase().includes('this group is not in that region')

  // If URL region changes, do not keep showing a group from a different region.
  useEffect(() => {
    if (!group || !isRegionMismatch) return
    toast.error('This group is not in that region.')
    router.replace(groupsHref)
  }, [group, isRegionMismatch, router, groupsHref])

  // If backend already rejected by region mismatch, redirect out of dead-end detail page.
  useEffect(() => {
    if (!isWrongRegionError) return
    toast.error('This group is not in that region.')
    router.replace(groupsHref)
  }, [isWrongRegionError, router, groupsHref])

  const isGroupContactRole = session ? GROUP_CONTACT_ROLES.includes(session.role as UserRole) : false
  const isLeaderOfThisGroup =
    group && session && group.contactUserId != null && Number(session.sub) === group.contactUserId
  const isMemberOfThisGroup =
    !!group &&
    !!session &&
    participants.some((p: Participant) => Number(p.id) === Number(session.sub))

  // V/TL/P may view this group only if they are the leader OR a participant; otherwise redirect
  // Must wait for participants to load before redirecting—otherwise we redirect on first render
  // when participants is still [] (race), then user has to click again to get in
  useEffect(() => {
    if (sessionLoading || !session || !group) return
    if (!isGroupContactRole) return
    if (isGroupContactRole && !isLeaderOfThisGroup && participantsLoading) return
    if (isLeaderOfThisGroup || isMemberOfThisGroup) return
    toast.error('You can only access groups you are part of.')
    router.replace(groupsHref)
  }, [group, session, sessionLoading, participantsLoading, router, isGroupContactRole, isLeaderOfThisGroup, isMemberOfThisGroup, groupsHref])

  if (!groupId) {
    return (
      <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
        <p className="text-muted-foreground">Invalid group.</p>
      </DashboardContentWrapper>
    )
  }

  if (isLoading || sessionLoading) {
    return (
      <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
        <p className="text-muted-foreground">Loading...</p>
      </DashboardContentWrapper>
    )
  }

  if (error || !group) {
    if (isWrongRegionError) {
      return (
        <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
          <p className="text-muted-foreground">Redirecting...</p>
        </DashboardContentWrapper>
      )
    }
    return (
      <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
        <div className="flex items-center gap-2">
          <GroupBackButton />
          <p className="text-muted-foreground">Group not found.</p>
        </div>
      </DashboardContentWrapper>
    )
  }

  if (isRegionMismatch) {
    return (
      <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
        <p className="text-muted-foreground">Redirecting...</p>
      </DashboardContentWrapper>
    )
  }

  // For V/TL/P who are not the leader, wait for participants to know if they're a member
  if (isGroupContactRole && !isLeaderOfThisGroup && participantsLoading) {
    return (
      <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
        <p className="text-muted-foreground">Loading...</p>
      </DashboardContentWrapper>
    )
  }

  // If V/TL/P and not leader and not member, we're redirecting (effect above); wait for participants to know
  const showRedirecting =
    isGroupContactRole &&
    !isLeaderOfThisGroup &&
    (!participantsLoading && !isMemberOfThisGroup)
  if (showRedirecting) {
    return (
      <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
        <p className="text-muted-foreground">Redirecting...</p>
      </DashboardContentWrapper>
    )
  }

  return (
    <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-center">
        <div className="flex items-center gap-0.5">
          <GroupBackButton />
          <Heading>{group.group_name} Group</Heading>
        </div>
        <LocationSelector />
      </div>
      <GroupDetailsTabs group={group} regionId={regionId} restrictToPreTripAndPhotos={false} />
      <ScrollToTopFab />
    </DashboardContentWrapper>
  )
}
